import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { selectErrorText, setFormValidity, setShowPaymentError } from './checkoutSlice'
import { EVENTS } from '../../analytics/events'
import { sendAnalyticsEvents } from '../../analytics/common'
import { selectEventsData } from '../events/eventsSlice'
import { fontSize, wh, tabletMinWidth } from '../../mixins'
import UIButton from '../../UI/UIButton'


const isArabic = navigator.language.startsWith('ar');
const textAlign = isArabic ? 'right' : 'left';

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  height: calc(var(--vh,1vh) * 100);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  background-color: #fff;
  padding: 16px 24px;
  z-index: 1000;
  isolation: isolate;
  
  @media screen and (min-width: ${tabletMinWidth}px) {
    justify-content: center;
  }
  
  &:before {
    content: '';
    display: block;
    ${wh('0px')};
  }
  
  &:after {
    content: '';
    display: block;
    width: 120vw;
    height: 120vh;
    position: absolute;
    top: -10vh;
    left: -10vw;
    background: #fff;
    z-index: -1;
  }
`;

const ModalContent = styled.div`
  max-width: 327px;
`;

const Title = styled.h1`
  font-weight: 600;
  ${fontSize(20)};
  line-height: 24px;
  text-align: center;
  color: #323232;
  margin: 0 0 8px;
`;

const ErrorText = styled.div`
  font-weight: 400;
  ${fontSize(14)};
  line-height: 17px;
  text-align: center;
  color: #ff5b5b;
  margin-bottom: 16px;
`;

const StyledList = styled.ul`
  padding: 0;
  margin: 0 0 24px 0;
  ${fontSize(16)};
  font-weight: 400;
  line-height: 19px;
  color: #323232;
  max-width: 310px;
  ${isArabic && 'direction: rtl;'}

  li {
    margin: ${isArabic ? '4px 20px 0 0' : '4px 0 0 20px'};
    position: relative;
    text-align: ${textAlign};
    
    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }
`;

const ErrorScreen = ({ clearFields }) => {
  const { t } = useTranslation();
  const errorText = useSelector(selectErrorText);
  const POINTS = ['your_card_balance', 'limits_on_card', 'contact_card_issue'];
  const eventsData = useSelector(selectEventsData);
  const dispatch = useDispatch();

  const renderCheckPoints = (point) => {
    return <li key={point}>{t(point)}</li>
  };

  const tryAgainClick = () => {
    clearFields();
    dispatch(setShowPaymentError({ show: false, text: '' }));
    dispatch(setFormValidity(null));
    sendAnalyticsEvents(EVENTS.onboardingDeclineTryAgain, eventsData);
  };

  return (
    <ModalWrapper>
      <ModalContent>
        <svg width="80" height="76" viewBox="0 0 80 76" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M34.8726 8.43682C37.2095 4.59162 42.7905 4.59162 45.1274 8.43682L69.1008 47.8839C71.5309 51.8824 68.6524 57 63.9735 57H16.0266C11.3476 57 8.46915 51.8824 10.8992 47.8839L34.8726 8.43682Z" fill="#FF5B5B"/>
          <path d="M40.0351 39.8922C39.5791 39.8922 39.2191 39.7482 38.9551 39.4602C38.7151 39.1722 38.5711 38.7642 38.5231 38.2362L37.4431 24.4842C37.3711 23.5962 37.5631 22.8882 38.0191 22.3602C38.4751 21.8082 39.1471 21.5322 40.0351 21.5322C40.8991 21.5322 41.5471 21.8082 41.9791 22.3602C42.4351 22.8882 42.6271 23.5962 42.5551 24.4842L41.4751 38.2362C41.4511 38.7642 41.3071 39.1722 41.0431 39.4602C40.8031 39.7482 40.4671 39.8922 40.0351 39.8922ZM40.0351 47.3802C39.2191 47.3802 38.5591 47.1282 38.0551 46.6242C37.5751 46.1202 37.3351 45.4722 37.3351 44.6802C37.3351 43.9122 37.5751 43.2882 38.0551 42.8082C38.5591 42.3042 39.2191 42.0522 40.0351 42.0522C40.8751 42.0522 41.5231 42.3042 41.9791 42.8082C42.4591 43.2882 42.6991 43.9122 42.6991 44.6802C42.6991 45.4722 42.4591 46.1202 41.9791 46.6242C41.5231 47.1282 40.8751 47.3802 40.0351 47.3802Z" fill="white"/>
        </svg>
        <Title>{t('payment_declined')}</Title>
        <ErrorText>{errorText}</ErrorText>
        <StyledList>{POINTS.map(renderCheckPoints)}</StyledList>
      </ModalContent>
      <UIButton onClick={tryAgainClick} bottomPosition={'24px'} customId={'try-again-button'} customText={t('try_again')} />
    </ModalWrapper>
  )
};

export default ErrorScreen